import React from 'react';
import PropTypes from 'prop-types';
import KnockButton from './KnockButton';
import GroupsButton from './GroupsButton';
import TileViewButton from './TileViewButton';
import WhiteboardButton from './WhiteboardButton';
import MoreButton from './MoreButton';
import Popover from '../Popover/Popover';
import BrowserButton from './BrowserButton';
import withBoundary from 'component/ErrorBoundary/withBoundary';
import { getAudioSettingsVisibility } from 'store/modules/settings';
import { SMALL_MOBILE_WIDTH } from 'store/modules/base/responsive-ui';
import { translate } from 'store/modules/base/i18n';
import { connect } from 'store/modules/base/redux';
import { getRemoteParticipantCount } from 'store/modules/base/participants';

/**
 * Button used for audio & audio settings.
 *
 * @returns {ReactElement}
 */
class MoreSettingsButton extends React.PureComponent {
  /**
   * Initializes a new {@code MoreSettingsButton} instance.
   *
   * @inheritdoc
   */
  constructor(props) {
    super(props);

    this._onEscClick = this._onEscClick.bind(this);
    this._onClick = this._onClick.bind(this);
    this.state = {
      showButtons: false
    };
  }

  /**
   * Click handler for the more actions entries.
   *
   * @param {KeyboardEvent} event - Esc key click to close the popup.
   * @returns {void}
   */
  _onEscClick(event) {
    if (event.key === 'Escape' && this.props.isOpen) {
      event.preventDefault();
      event.stopPropagation();
      this._onClick();
    }
  }

  /**
   * Click handler for the more actions entries.
   *
   * @returns {void}
   */
  _onClick() {
    this.setState({
      showButtons: !this.state.showButtons
    });
  }

  /**
   * Implements React's {@link Component#render}.
   *
   * @inheritdoc
   */
  render() {
    const { popupPlacement, buttonKey, notifyMode, _user, _devices } =
      this.props;

    return (
      <Popover
        className="toolbox-item"
        content={
          <div
            style={{
              width: 80,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center'
            }}>
            {_user.device?.options?.showKnockButton && (
              <KnockButton showLabel={false} />
            )}
            {_user.device?.options?.showViewButton && _devices > 1 && (
              <TileViewButton showLabel={false} />
            )}
            {_user.device?.options?.showWhiteboardButton && (
              <WhiteboardButton showLabel={false} />
            )}
            {_user.device?.allowBrowser &&
            _user.device?.browserOptions?.length > 0
              ? _user.device?.browserOptions.map((item, index) => {
                  return (
                    <BrowserButton
                      options={item}
                      showLabel={false}
                      key={index}
                    />
                  );
                })
              : null}
            {_user.device?.groups?.length > 1 && (
              <GroupsButton showLabel={false} />
            )}
          </div>
        }
        onPopoverClose={this._onClick}
        position={popupPlacement}
        trigger="click"
        visible={this.state.showButtons}>
        <MoreButton
          buttonKey={buttonKey}
          notifyMode={notifyMode}
          onIconClick={this._onClick}
          onIconKeyDown={this._onEscClick}
        />
      </Popover>
    );
  }
}

/**
 * Function that maps parts of Redux state tree into component props.
 *
 * @param {Object} state - Redux state.
 * @returns {Object}
 */
function mapStateToProps(state) {
  const { user } = state['features/login'];
  const { permissions = {} } = state['features/base/devices'];
  const { clientWidth } = state['features/base/responsive-ui'];

  return {
    _user: user,
    _devices: getRemoteParticipantCount(state),
    hasPermissions: permissions.audio,
    isDisabled: false,
    isOpen: getAudioSettingsVisibility(state),
    popupPlacement: clientWidth <= SMALL_MOBILE_WIDTH ? 'auto' : 'top-center',
    _userIdleMode: state['features/settings'].userIdleMode
  };
}

MoreSettingsButton.propTypes = {
  /**
   * The button's key.
   */
  buttonKey: PropTypes.string,

  /**
   * External handler for click action.
   */
  handleClick: PropTypes.func,

  /**
   * Indicates whether audio permissions have been granted or denied.
   */
  hasPermissions: PropTypes.bool,

  /**
   * Click handler for the small icon. Opens audio options.
   */
  onAudioOptionsClick: PropTypes.func,
  popupPlacement: PropTypes.any,

  /**
   * If the button should be disabled.
   */
  isDisabled: PropTypes.bool,

  /**
   * Notify mode for `toolbarButtonClicked` event -
   * whether to only notify or to also prevent button click routine.
   */
  notifyMode: PropTypes.string,

  /**
   * Used for translation.
   */
  t: PropTypes.func,

  /**
   * Flag controlling the visibility of the button.
   * AudioSettings popup is disabled on mobile browsers.
   */
  visible: PropTypes.bool,

  /**
   * Defines is popup is open.
   */
  isOpen: PropTypes.bool,
  _devices: PropTypes.any,
  _user: PropTypes.any
};

export default React.memo(
  translate(connect(mapStateToProps)(withBoundary(MoreSettingsButton)))
);
